/**
 * useProjects Hook
 * Custom hook para gestionar proyectos con API real
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsService, type Project } from '@/services/api/projectsService';
import { useAuth } from '@/shared/contexts/AuthContext';

/**
 * Hook para obtener todos los proyectos
 */
export function useProjects(params?: Record<string, any>) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['projects', params],
    queryFn: () => projectsService.getProjects(params),
    enabled: isAuthenticated,
    staleTime: 1 * 60 * 1000, // 1 minuto (reducido para refrescar más rápido)
    gcTime: 10 * 60 * 1000, // 10 minutos en cache
    refetchOnMount: 'always', // Siempre refetch al montar (para captar cambios en status_colors)
    refetchOnWindowFocus: false, // NO refetch al volver a la ventana
  });
}

/**
 * Hook para obtener proyectos con paginación server-side
 */
export function useProjectsPaginated(
  params?: Record<string, any>,
  page: number = 1,
  pageSize: number = 50
) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['projects-paginated', params, page, pageSize],
    queryFn: () => projectsService.getProjects({
      ...params,
      page,
      page_size: pageSize,
    }),
    enabled: isAuthenticated,
    staleTime: 1 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook para obtener un proyecto específico
 */
export function useProject(id: string | undefined, params?: Record<string, string>) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['projects', id, params],
    queryFn: async () => {
      try {
        const project = await projectsService.getProjectById(id!, params);
        return project;
      } catch (error) {
        console.error('❌ [useProject] Error fetching project:', error);
        if (error instanceof Error && error.message.includes('CancelledError')) {
          throw error;
        }
        throw error;
      }
    },
    enabled: isAuthenticated && !!id,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('CancelledError')) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: 1000,
    throwOnError: (error) => {
      if (error instanceof Error && error.message.includes('CancelledError')) {
        return false;
      }
      return true;
    },
  });
}

/**
 * Hook para obtener estadísticas de un proyecto
 */
export function useProjectStats(id: string | undefined) {
  const { isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: ['projects', id, 'stats'],
    queryFn: () => projectsService.getProjectStats(id!),
    enabled: isAuthenticated && !!id,
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
}

/**
 * Hook para obtener unidades de un proyecto
 */
export function useProjectUnits(id: string | undefined) {
  const { isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: ['projects', id, 'units'],
    queryFn: () => projectsService.getProjectUnits(id!),
    enabled: isAuthenticated && !!id,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook para obtener unidades disponibles de un proyecto
 */
export function useAvailableUnits(id: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['projects', id, 'available-units'],
    queryFn: () => projectsService.getAvailableUnits(id!),
    enabled: isAuthenticated && !!id,
    staleTime: 1 * 60 * 1000, // 1 minuto (datos más volátiles)
  });
}

/**
 * Hook para obtener amenities de un proyecto
 */
export function useProjectAmenities(id: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['projects', id, 'amenities'],
    queryFn: () => projectsService.getProjectAmenities(id!),
    enabled: isAuthenticated && !!id,
    staleTime: 3 * 60 * 1000, // 3 minutos
  });
}

/**
 * Hook para obtener proyectos destacados
 */
export function useFeaturedProjects() {
  const { isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: ['projects', 'featured'],
    queryFn: () => projectsService.getFeaturedProjects(),
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000, // 10 minutos
  });
}

/**
 * Hook para crear proyecto
 */
export function useCreateProject() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: Partial<Project>) => projectsService.createProject(data),
    onSuccess: (newProject) => {
      // Invalidar todas las queries de proyectos
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      // También invalidar la compañía para actualizar el contador de proyectos
      if (newProject.company) {
        queryClient.invalidateQueries({ queryKey: ['companies', newProject.company] });
      }
    },
  });
}

/**
 * Hook para actualizar proyecto
 */
export function useUpdateProject() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Project> }) =>
      projectsService.updateProject(id, data),
    onSuccess: (updatedProject, variables) => {

      // 1. Actualizar cache con la respuesta del PATCH (UI se actualiza instantáneamente)
      // Update both possible query key shapes: with and without params
      queryClient.setQueryData(['projects', variables.id], updatedProject);
      queryClient.setQueryData(['projects', variables.id, undefined], updatedProject);

      // 2. Invalidar en paralelo (background sync, sin bloquear UI)
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['projects', variables.id],
          exact: false, // Invalidate all variants of this project's query
        }),
        queryClient.invalidateQueries({
          queryKey: ['projects'],
          exact: true, // Solo la lista
        }),
        queryClient.invalidateQueries({
          queryKey: ['tenant-projects-platform'],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['tenant-projects-accessible'],
          exact: false,
        }),
      ]);

    },
    onError: (error, variables) => {
      // Invalidar para intentar refrescar
      queryClient.invalidateQueries({ queryKey: ['projects', variables.id] });
    },
  });
}

/**
 * Hook para eliminar proyecto
 */
export function useDeleteProject() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => projectsService.deleteProject(id),
    onSuccess: () => {
      // Invalidar cache de proyectos
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

